import { type ReactNode, useState } from "react";
import { toast } from "sonner";

import {
  type CarrierCustomerConnection,
  type CarrierCustomerTier,
  type CarrierLabelDiscountRule,
  usePatchCarrierCustomer,
} from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { formatDateTime, formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";
import { CustomerOverrideButton } from "./discount-rule-editors";
import {
  customerOverrideRule,
  ruleEffectiveStatus,
} from "./discount-rule-resolution";
import {
  type CustomerDiscountsMode,
  customerName,
  customerStatus,
  formatOptionalPercent,
} from "./customer-view-model";
import {
  customerStatusMutationLocks,
  mutationFailure,
  useSubmissionGuard,
} from "./mutation-guards";

export function CustomerDetailDrawer({
  open,
  onOpenChange,
  row,
  tiers,
  isOwner,
  mode,
  rules = [],
  tierAssignmentPending = false,
  tierValue,
  onAssignTier,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: CarrierCustomerConnection;
  tiers: CarrierCustomerTier[];
  isOwner: boolean;
  mode: CustomerDiscountsMode;
  rules?: CarrierLabelDiscountRule[];
  tierAssignmentPending?: boolean;
  tierValue?: string;
  onAssignTier?: (tierId: string) => void;
}) {
  const overrideRule = customerOverrideRule(rules, row.customerId, row.tierId);
  const status = customerStatus(row);
  const tierName =
    row.tier?.name ??
    tiers.find((tier) => tier.id === row.tierId)?.name ??
    "Default";
  const canManage = isOwner && !row.removed;
  const canOverride = canManage && status === "ACTIVE";

  return (
    <DetailDrawer
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && tierAssignmentPending) return;
        onOpenChange(nextOpen);
      }}
      title={customerName(row)}
      description={row.customer.email ?? "Email unavailable"}
    >
      <div className="space-y-5 text-sm">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border p-4">
          <CustomerDetailField
            label="Status"
            value={<StatusBadge status={status} />}
          />
          <CustomerDetailField label="Orders" value={row.orderCount} />
          <CustomerDetailField label="Tier" value={tierName} />
          <CustomerDetailField
            label="Effective discount"
            value={formatOptionalPercent(row.effectiveDiscountPercentage)}
          />
          <CustomerDetailField
            label="Added"
            value={formatDateTime(row.addedAt)}
          />
          <CustomerDetailField
            label="Blocked"
            value={formatDateTime(row.blockedAt)}
          />
          <CustomerDetailField
            label="Left"
            value={formatDateTime(row.customerRemovedAt)}
          />
          <CustomerDetailField label="Connection ID" value={row.id} mono />
          {row.blockedReason && (
            <CustomerDetailField
              label="Blocked reason"
              value={row.blockedReason}
              className="col-span-2"
            />
          )}
        </dl>

        {mode === "customers" && (
          <section
            className="space-y-3"
            aria-labelledby="customer-access-heading"
          >
            <h3
              id="customer-access-heading"
              className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Membership and access
            </h3>
            <div>
              <label
                htmlFor={`customer-tier-${row.id}`}
                className="mb-1 block font-medium"
              >
                Tier
              </label>
              <NativeSelect
                id={`customer-tier-${row.id}`}
                value={tierValue ?? row.tierId ?? ""}
                disabled={!canManage || tierAssignmentPending}
                onChange={(event) => onAssignTier?.(event.target.value)}
              >
                <option value="">Default</option>
                {tiers.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            {canManage && <CustomerBlockButton row={row} fullWidth />}
          </section>
        )}

        {mode === "discounts" && (
          <section
            className="space-y-3"
            aria-labelledby="customer-discount-heading"
          >
            <h3
              id="customer-discount-heading"
              className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Customer override
            </h3>
            {overrideRule ? (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border p-4">
                <CustomerDetailField
                  label="Percentage"
                  value={formatPercent(overrideRule.percentage)}
                />
                <CustomerDetailField
                  label="Effective state"
                  value={
                    <StatusBadge status={ruleEffectiveStatus(overrideRule)} />
                  }
                />
                <CustomerDetailField
                  label="Starts"
                  value={formatDateTime(overrideRule.startsAt)}
                />
                <CustomerDetailField
                  label="Ends"
                  value={formatDateTime(overrideRule.endsAt)}
                />
              </dl>
            ) : (
              <p className="rounded-lg border p-4 text-muted-foreground">
                No customer-specific override. The tier or carrier default is
                used.
              </p>
            )}
            {canOverride && (
              <CustomerOverrideButton
                customerId={row.customerId}
                customerLabel={customerName(row)}
                existing={overrideRule}
                fullWidth
              />
            )}
          </section>
        )}
      </div>
    </DetailDrawer>
  );
}

function CustomerDetailField({
  label,
  value,
  mono = false,
  className,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className={cn("mt-1 break-words", mono && "font-mono text-xs")}>
        {value}
      </dd>
    </div>
  );
}

export function CustomerBlockButton({
  row,
  fullWidth = false,
}: {
  row: CarrierCustomerConnection;
  fullWidth?: boolean;
}) {
  const patch = usePatchCarrierCustomer();
  const submission = useSubmissionGuard();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const blocked = row.status === "BLOCKED";
  const pending = submission.pending || patch.isPending;

  async function submit() {
    if (customerStatusMutationLocks.has(row.customerId)) return;
    customerStatusMutationLocks.add(row.customerId);
    try {
      await submission.run(async () => {
        setErrorMessage(null);
        try {
          await patch.mutateAsync({
            customerId: row.customerId,
            block: {
              blocked: !blocked,
              reason: blocked ? null : "Blocked from carrier portal",
            },
          });
          toast.success(blocked ? "Customer unblocked." : "Customer blocked.");
        } catch (error) {
          const message = mutationFailure(
            error,
            "update this customer's status"
          );
          setErrorMessage(message);
          toast.error(message);
        }
      });
    } finally {
      customerStatusMutationLocks.delete(row.customerId);
    }
  }

  return (
    <div className={cn(fullWidth && "space-y-2")}>
      {errorMessage && fullWidth && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage}
        </p>
      )}
      <Button
        size={fullWidth ? "touch" : "sm-touch"}
        variant="outline"
        className={cn(fullWidth && "w-full")}
        disabled={pending}
        onClick={() => void submit()}
      >
        {pending
          ? blocked
            ? "Unblocking..."
            : "Blocking..."
          : blocked
            ? "Unblock"
            : "Block"}
      </Button>
    </div>
  );
}
